/**
 * Vue plugin: registers global Axios request + auth response interceptors
 * on the shared api instance (src/service/api.js).
 *
 * Flow on auth failure (token expired / 401 / auth-related 403 / JWT messages):
 * 1. Response interceptor detects the failure (see helpers/authSession.js)
 * 2. Clears access_token and related session storage once (isLoggingOut guard)
 * 3. Clears Pinia auth state when available
 * 4. Shows toast: "Your session has expired. Please login again."
 * 5. Redirects to /login (skipped if already on the login page)
 *
 * Individual API service methods are unchanged — all traffic through `api` is covered.
 */
import axiosInstance from './api';
import router from '@/router';
import { createToaster } from '@meforma/vue-toaster';
import {
    attachAuthResponseInterceptor,
    getAccessToken,
    SESSION_EXPIRED_MESSAGE
} from '@/helpers/authSession';

const toaster = createToaster({ position: 'top-right', duration: 4000 });

function showSessionExpiredToast(message) {
    toaster.error(message || SESSION_EXPIRED_MESSAGE);
}

function attachRequestInterceptor() {
    if (axiosInstance.__briktokRequestInterceptorAttached) {
        return;
    }
    axiosInstance.__briktokRequestInterceptorAttached = true;

    axiosInstance.interceptors.request.use(
        (config) => {
            const token = getAccessToken();

            // Preserve multipart when callers override Content-Type on the request config.
            const isMultipart =
                config.headers &&
                (config.headers['Content-Type'] === 'multipart/form-data' ||
                    config.headers['content-type'] === 'multipart/form-data' ||
                    config['Content-Type'] === 'multipart/form-data');

            config.headers = config.headers || {};
            if (!isMultipart && !config.headers['Content-Type'] && !config.headers['content-type']) {
                config.headers['Content-Type'] = 'application/json';
            }

            config.headers['Cache-Control'] = 'no-cache, no-store';
            config.headers['Pragma'] = 'no-cache';

            if (token && !config.headers.Authorization && !config.headers.authorization) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        },
        (error) => Promise.reject(error)
    );
}

const setupInterceptors = {
    install() {
        // Request: attach Bearer access_token from localStorage
        attachRequestInterceptor();

        // Response: centralized logout on expired / invalid token (see authSession.js)
        attachAuthResponseInterceptor(axiosInstance, {
            router,
            showToast: showSessionExpiredToast
        });
    }
};

export default setupInterceptors;
